import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import { breakpoints } from 'client/utils/bootstrap';
import { Carousel } from 'site-modules/shared/components/carousel/carousel';
import { CmsEntities } from 'client/data/models/cms';
import { DEFAULT_CONTENT } from 'client/data/cms/content';
import { TrackingConstant } from 'client/tracking/constant';
import { useScrollIntoViewTracking } from 'site-modules/shared/hooks/use-scroll-into-view-tracking';
import { getStaticImageUrl } from 'client/utils/image-helpers';

import './appraisal-user-testimonials.scss';

const RESPONSIVE_OPTIONS = [
  { breakpoint: breakpoints.sm, settings: { slidesToShow: 1, slidesToScroll: 1 } },
  { breakpoint: breakpoints.lg, settings: { slidesToShow: 3, slidesToScroll: 3 } },
];

const MOBILE_SLIDE_OPTIONS = { slidesToShow: 1 };

const DESKTOP_SLIDE_OPTIONS = {
  slidesToShow: 3,
  slidesToScroll: 3,
};

const CREATIVE_ID = 'edm-entry-epo-testimonials';

export function AppraisalUserTestimonialsUI({
  isMobile = false,
  testimonials = DEFAULT_CONTENT,
  subheading = '',
  title = 'What people say',
  className = '',
  titleClassName = 'size-24 fw-bold mb-0_25',
}) {
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const slideOptions = isMobile ? MOBILE_SLIDE_OPTIONS : DESKTOP_SLIDE_OPTIONS;
  const testimonialsData = testimonials.children().map(test => test.getAllMetadata());
  const shouldRender = testimonialsData.length > 0;
  const slidesToShow = slideOptions.slidesToShow;
  const customPaging = useCallback(i => <button aria-label={`Slide ${i + 1}`} />, []);
  const onBeforeChange = useCallback((from, to) => setCurrentSlideIndex(to), []);

  const ref = useRef(null);
  useScrollIntoViewTracking({
    eventData: {
      event_type: TrackingConstant.ACTION_WIDGET_VIEW,
      event_data: {
        action_cause: TrackingConstant.ACTION_CAUSE_SCROLL,
        creative_id: CREATIVE_ID,
      },
    },
    additionalConditions: shouldRender,
    ref,
  });

  if (!shouldRender) return null;

  return (
    <div
      data-tracking-parent={CREATIVE_ID}
      data-no-widget-view // widget_view is fired using useScrollIntoViewTracking hook
      className={classnames('appraisal-user-testimonials', className)}
      ref={ref}
    >
      <h2 className={classnames(titleClassName)}>{title}</h2>
      {!!subheading && <div className="testimonials-subheading size-16 mb-1_5">{subheading}</div>}
      <div className="d-flex justify-content-center">
        <div className="testimonial-carousel-container">
          <Carousel
            swipe={isMobile}
            arrows={!isMobile}
            infinite={false}
            responsive={RESPONSIVE_OPTIONS}
            dots
            dotsClass="slick-dots gray mt-1_5"
            speed={500}
            cssEase="ease-in-out"
            className="testimonial-carousel"
            customEventData={{
              subaction_name: 'view_testimonial',
              action_cause: 'link_click',
              value: '',
            }}
            customPaging={customPaging}
            beforeChange={onBeforeChange}
            {...slideOptions}
          >
            {testimonialsData.map(({ testimonialTitle, testimonialText, date, name, location }, i) => {
              const startIndexOfSet = Math.floor(currentSlideIndex / slidesToShow) * slidesToShow;
              const isCurrentSlide = i === startIndexOfSet; // Only the first slide in each set is marked as current
              const isStartIndexOfSet = i % slidesToShow === 0;

              return (
                <div className="h-100 pe-1 pe-md-0" key={testimonialTitle}>
                  {isStartIndexOfSet && (
                    <span className="visually-hidden" aria-current={isCurrentSlide}>
                      Page {Math.floor(i / slidesToShow) + 1}
                    </span>
                  )}
                  <div className="h-100 gradient-border rounded-16">
                    <div className="d-flex flex-column justify-content-between bg-white h-100 user-testimonial-card px-1 pt-1_5 pb-1 rounded-16">
                      <div className="mb-1_5">
                        <h3 className="testimonial-header heading-5 mb-1">{testimonialTitle}</h3>
                        <div className="mt-0_5">{testimonialText}</div>
                      </div>
                      <div className="user-info size-12 d-flex justify-content-between">
                        <div>
                          <span className="fw-medium size-16">{name}</span>
                          <div>{location}</div>
                          <div className="testimonial-date">{date}</div>
                        </div>
                        <div>
                          <img
                            src={getStaticImageUrl('appraisal/quote.svg')}
                            alt=""
                            width={73}
                            height={46}
                            loading="lazy"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Carousel>
        </div>
      </div>
    </div>
  );
}

AppraisalUserTestimonialsUI.propTypes = {
  isMobile: PropTypes.bool,
  testimonials: CmsEntities.Content,
  subheading: PropTypes.string,
  title: PropTypes.string,
  className: PropTypes.string,
  titleClassName: PropTypes.string,
};

export const mapStateToProps = state => ({
  isMobile: state.mobile,
});

export const AppraisalUserTestimonials = connect(mapStateToProps)(AppraisalUserTestimonialsUI);
